"use client";

import posthog from "posthog-js";

/**
 * Every client-side product event. Keeping the names in one union stops typos from silently
 * creating new event names in PostHog, which would split a funnel's history.
 */
export type AnalyticsEvent =
  // Auth & identity
  | "user_logged_out"
  // PWA
  | "pwa_launched"
  | "pwa_install_prompt_available"
  | "pwa_install_prompt_result"
  | "pwa_installed"
  | "app_offline_retry_clicked"
  // Onboarding / trial
  | "get_started_viewed"
  | "trial_activation_started"
  | "trial_activation_failed"
  | "onboarding_cta_clicked"
  // Preferences
  | "preferences_saved"
  | "job_history_cleared"
  | "custom_prompt_validated"
  | "custom_prompt_backtested"
  | "custom_prompt_accepted"
  | "custom_prompt_toggled"
  // Notifications
  | "push_permission_result"
  | "notification_channel_toggled"
  | "device_push_toggled"
  | "device_removed"
  | "marketing_emails_toggled"
  // Upsell & payments
  | "upsell_viewed"
  | "upsell_clicked"
  | "billing_address_required"
  | "full_name_required"
  | "billing_address_saved"
  | "wallet_balance_toggled"
  | "purchase_failed"
  | "payment_success_viewed"
  // Feed
  | "feed_switched"
  | "feed_page_loaded"
  | "feed_empty_viewed"
  | "feed_error_viewed"
  | "feed_end_reached"
  | "job_search_submitted"
  | "job_card_badge_expanded"
  | "preferences_cta_clicked"
  // Growth & account
  | "referral_code_copied"
  | "referral_shared"
  | "account_suspended"
  | "profile_updated"
  | "landing_cta_clicked"
  | "contact_form_submitted"
  | "report_issue_submitted";

/** Which trial-activate button was used. */
export type TrialActivationSurface = "start_trial_top" | "start_trial_bottom";

/** Where the browser push permission prompt was triggered from. */
export type PushPermissionSurface = "settings" | "pwa_auto_prompt" | "permission_prompt";

export type AnalyticsProperties = Record<
  string,
  string | number | boolean | null | undefined | string[]
>;

/** Captures a product event. Never throws: analytics must not break a user flow. */
export function track(event: AnalyticsEvent, properties?: AnalyticsProperties) {
  if (typeof window === "undefined") return;
  try {
    posthog.capture(event, properties);
  } catch {
    // Analytics failures are never user-facing.
  }
}

/** Sets person properties on the identified user (no event of its own). */
export function setPersonProperties(
  properties: AnalyticsProperties,
  setOnceProperties?: AnalyticsProperties
) {
  if (typeof window === "undefined") return;
  try {
    posthog.setPersonProperties(properties, setOnceProperties);
  } catch {
    // Analytics failures are never user-facing.
  }
}

/**
 * Clears the PostHog identity so the next person on this device is not merged into the previous
 * user. Call after sign-out (logout, suspend).
 */
export function resetAnalytics() {
  if (typeof window === "undefined") return;
  try {
    posthog.reset();
  } catch {
    // Analytics failures are never user-facing.
  }
}
